// LoadingContext.js
import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';

// Create context
const LoadingContext = createContext();

// Create a reference to store loading functions
// This will be accessible outside of React components
const loadingUtils = {
  startLoading: null,
  stopLoading: null,
  isLoading: false
};

// Define actions
const ADD_LOADER = 'ADD_LOADER';
const REMOVE_LOADER = 'REMOVE_LOADER';
const SET_FIRST_UNLOAD_HAPPENED = 'SET_FIRST_UNLOAD_HAPPENED';

// Reducer to manage loading state
const loadingReducer = (state, action) => {
  switch (action.type) {
    case ADD_LOADER:
      return {
        ...state,
        loaders: {
          ...state.loaders,
          [action.id]: true
        }
      };
    case REMOVE_LOADER:
      const newLoaders = { ...state.loaders };
      delete newLoaders[action.id];
      return {
        ...state,
        loaders: newLoaders
      };
    case SET_FIRST_UNLOAD_HAPPENED:
      return {
        ...state,
        hasFirstUnloadHappened: true
      };
    default:
      return state;
  }
};

// Provider component
export const LoadingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(loadingReducer, { 
    loaders: {},
    hasFirstUnloadHappened: false
  });
  const isFirstRender = useRef(true);
  const prevIsLoading = useRef(false);

  // Check if any loader is active
  const isLoading = Object.keys(state.loaders).length > 0;

  // Track when loading goes from true to false for the first time
  React.useEffect(() => {
    if (prevIsLoading.current === true && isLoading === false && !state.hasFirstUnloadHappened) {
      dispatch({ type: SET_FIRST_UNLOAD_HAPPENED });
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, state.hasFirstUnloadHappened]);

  // isFirstLoad remains true until the first unload happens
  const isFirstLoad = !state.hasFirstUnloadHappened;

  // Add a loader
  const addLoader = useCallback((id) => {
    dispatch({ type: ADD_LOADER, id });
    return id;
  }, []);

  // Remove a loader
  const removeLoader = useCallback((id) => {
    dispatch({ type: REMOVE_LOADER, id });
  }, []);

  // Create a unique loader ID and start loading
  const startLoading = useCallback(() => {
    const id = Date.now().toString();
    addLoader(id);
    return id;
  }, [addLoader]);

  // Stop loading with the given ID
  const stopLoading = useCallback((id) => {
    removeLoader(id);
  }, [removeLoader]);
  
  // Update the loadingUtils reference with the latest functions
  // This allows utility functions to access these methods
  React.useEffect(() => {
    loadingUtils.startLoading = startLoading;
    loadingUtils.stopLoading = stopLoading;
    loadingUtils.isLoading = isLoading;
    
    // Clean up on unmount
    return () => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      
      // Only clear if this is the provider that set these functions
      // This prevents issues with multiple providers
      if (
        loadingUtils.startLoading === startLoading &&
        loadingUtils.stopLoading === stopLoading
      ) {
        loadingUtils.startLoading = null;
        loadingUtils.stopLoading = null;
        loadingUtils.isLoading = false;
      }
    };
  }, [startLoading, stopLoading, isLoading]);
  return (
    <LoadingContext.Provider
      value={{ isLoading, startLoading, stopLoading }}
    >
      {children}
      {isLoading && <LoadingOverlay isFirstLoad={isFirstLoad} />}
    </LoadingContext.Provider>
  );
};

// Custom hook to use the loading context
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

// Loading overlay component
const LoadingOverlay = ({ isFirstLoad }) => {
  const renderCount = React.useRef(0);
  renderCount.current++;
  //console.log('LoadingOverlay rendered', renderCount.current, 'times', 'isFirstLoad:', isFirstLoad);
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isFirstLoad ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          border: '2px solid #f3f3f3',
          borderTop: '2px solid #3498db',
          animation: 'spin 1s linear infinite',
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Export the loading utilities for use in non-React files
export const LoadingService = {
  startLoading: () => {
    if (!loadingUtils.startLoading) {
      console.warn('LoadingProvider not initialized. Make sure it is mounted in your component tree.');
      return null;
    }
    return loadingUtils.startLoading();
  },
  
  stopLoading: (id) => {
    if (!loadingUtils.stopLoading) {
      console.warn('LoadingProvider not initialized. Make sure it is mounted in your component tree.');
      return;
    }
    loadingUtils.stopLoading(id);
  },
  
  getLoadingState: () => {
    return loadingUtils.isLoading;
  }
};